/* eslint-disable import/no-duplicates */
import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { format, parseISO } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';

import { Container, Button, Row, Col, Badge } from 'react-bootstrap';

import api from '../../../services/api';

import {
  formatPrice,
  ORIGEM_PAGAMENTO,
  TIPO_USUARIO,
} from '../../../utils/funcoes';

import { IOptions } from '../../../utils/options';

import { useToast } from '../../../hooks/toast';
import { useClinica } from '../../../hooks/clinica';
import { useAuth } from '../../../hooks/auth';

import Input from '../../../components/Input';
import SelectSync from '../../../components/SelectSync';
import Loading from '../../../components/Loading';

import Modal from '../../../components/Modal';
import Pagamento from '../../../components/Pagamento';

import getValidationErrors from '../../../utils/getValidationErrors';

import getResponseErrors from '../../../utils/getResponseErrors';
import { IDadosPagamento } from '../../CartaoSaudeDetalhe';
import HeaderRelatorio from '../HeaderRelatorio';
import PageInfo from '../../../components/PageInfo';

interface IItem {
  codigo: number;
  descricao: string;
  valor: number;
  valorFormatado: string;
}

interface IRelatorio {
  despesas: IItem[];
  totalDespesas: number;
  totalDespesasFormatado: string;
  receitas: IItem[];
  totalReceitas: number;
  totalReceitasFormatado: string;
  trocos: IItem[];
  sangrias: IItem[];
  totalCaixaFormatado: string;
  totalGeral: number;
  totalGeralFormatado: string;
}

interface IFormBusca {
  data: Date;
  CliCodigo: string;
  UsuCodigo: string;
}

interface IDataExibe {
  dataHora: string;
  dataDiaSemana: string;
}

interface IDadosPagamentoOrigem extends IDadosPagamento {
  CprOrigem: number;
}

const FechamentoCaixa: React.FC = () => {
  const history = useHistory();
  const { addToast } = useToast();
  const { user } = useAuth();

  const [showModalPagamento, setShowModalPagamento] = useState(false);

  const { optionsClinica } = useClinica();

  const formRefBusca = useRef<FormHandles>(null);
  const [loading, setLoading] = useState(false);
  const [dadosRelatorio, setDadosRelatorio] = useState<IRelatorio>();
  const [dataExibe, setDataExibe] = useState<IDataExibe>();
  const [nomeClinica, setNomeClinica] = useState('');
  const [nomeUsuario, setNomeUsuario] = useState('');
  const [dadosPagamento, setDadosPagamento] = useState<
    IDadosPagamentoOrigem | undefined
  >();
  const [optionsUsuario, setOptionsUsuario] = useState<IOptions[]>([]);
  const [loadingUsuario, setLoadingUsuario] = useState(false);

  // verificar esta permissão
  useEffect(() => {
    if (user.UsuTipo > TIPO_USUARIO.SUPERVISOR) {
      history.push('/sempermissao');
    }
  }, [history, user.UsuTipo]);

  const informacoesPagina = `<b>Descrição:</b> Relatório do espelho do caixa, com todos os pagamentos recebidos no dia e todas as despesas lançadas. `;

  const initialData = {
    data: format(new Date(), 'yyyy-MM-dd'),
    CliCodigo: user.UsuClinica.toString(),
  };

  const handleBuscaUsuariosBaixa = useCallback(
    async (dados: IFormBusca) => {
      try {
        setLoadingUsuario(true);
        const dataFormatada = format(dados.data, 'yyyy-MM-dd');

        const response = await api.get<IOptions[]>(
          `/relatorios/usuariosfechamentocaixa?data=${dataFormatada}&CliCodigo=${dados.CliCodigo}`,
        );

        setOptionsUsuario(response.data);
      } catch (error) {
        console.log(error);
      } finally {
        setLoadingUsuario(false);
      }
    },

    [],
  );

  useEffect(() => {
    handleBuscaUsuariosBaixa({
      CliCodigo: user.UsuClinica.toString(),
      UsuCodigo: '0',
      data: new Date(),
    });
  }, [handleBuscaUsuariosBaixa, user.UsuClinica]);

  const handleChangeData = useCallback(() => {
    if (formRefBusca && formRefBusca.current) {
      const dataSelecionada = String(
        formRefBusca.current.getFieldValue('data'),
      );

      if (dataSelecionada.length !== 10) return;

      const CliCodigo = String(formRefBusca.current.getFieldValue('CliCodigo'));

      handleBuscaUsuariosBaixa({
        CliCodigo,
        UsuCodigo: '0',
        data: parseISO(dataSelecionada),
      });
    }
  }, [handleBuscaUsuariosBaixa]);

  const handleChangeClinica = useCallback(
    ({ value, label }) => {
      if (formRefBusca && formRefBusca.current) {
        const dataSelecionada = String(
          formRefBusca.current.getFieldValue('data'),
        );

        if (dataSelecionada.length !== 10) return;

        handleBuscaUsuariosBaixa({
          CliCodigo: value,
          UsuCodigo: '0',
          data: parseISO(dataSelecionada),
        });
      }
    },
    [handleBuscaUsuariosBaixa],
  );

  const handleSubmitBusca = useCallback(
    async (dados: IFormBusca) => {
      setLoading(true);
      try {
        formRefBusca.current?.setErrors({});
        const schema = Yup.object().shape({
          data: Yup.date().required(),
          CliCodigo: Yup.string().required(),
          UsuCodigo: Yup.string().nullable(true),
        });

        await schema.validate(dados, {
          abortEarly: false,
        });

        setNomeClinica(
          optionsClinica.find(cli => cli.value === dados.CliCodigo)?.label ||
            '',
        );

        const dataSelecionada = parseISO(dados.data.toString());

        const dataFormatada = format(dataSelecionada, 'yyyy-MM-dd');

        setDataExibe({
          dataDiaSemana: format(dataSelecionada, 'dd/MM/yyyy EEEE', {
            locale: ptBR,
          }).toUpperCase(),
          dataHora: format(new Date(), 'dd/MM/yyyy HH:mm:ss'),
        });

        const usuario = dados.UsuCodigo ? `&UsuCodigo=${dados.UsuCodigo}` : '';

        setNomeUsuario(
          optionsUsuario.find(cli => cli.value === dados.UsuCodigo)?.label ||
            '',
        );

        const response = await api.get<IRelatorio>(
          `/relatorios/fechamentocaixa?data=${dataFormatada}&CliCodigo=${dados.CliCodigo}${usuario}`,
        );

        const totalDespesas = response.data.despesas.reduce((tot, item) => {
          return tot + Number(item.valor);
        }, 0);

        const totalReceitas = response.data.receitas.reduce((tot, item) => {
          return tot + Number(item.valor);
        }, 0);

        const totalDinheiroReceitas =
          response.data.receitas.find(
            item => item.descricao.toLowerCase() === 'dinheiro',
          )?.valor || 0;

        const totalTrocos = response.data.trocos.reduce((tot, item) => {
          return tot + Number(item.valor);
        }, 0);

        const totalSangrias = response.data.sangrias.reduce((tot, item) => {
          return tot + Number(item.valor);
        }, 0);

        const totalCaixa =
          totalDinheiroReceitas - totalSangrias + totalTrocos - totalDespesas;

        const dadosFormatados: IRelatorio = {
          ...response.data,
          despesas: response.data.despesas.map(des => {
            return { ...des, valorFormatado: formatPrice(des.valor) };
          }),
          receitas: response.data.receitas.map(des => {
            return { ...des, valorFormatado: formatPrice(des.valor) };
          }),
          trocos: response.data.trocos.map(des => {
            return { ...des, valorFormatado: formatPrice(des.valor) };
          }),
          sangrias: response.data.sangrias.map(des => {
            return { ...des, valorFormatado: formatPrice(des.valor) };
          }),
          totalDespesas,
          totalDespesasFormatado: formatPrice(totalDespesas),
          totalReceitas,
          totalReceitasFormatado: formatPrice(totalReceitas),
          totalGeral: totalReceitas - totalDespesas,
          totalGeralFormatado: formatPrice(totalReceitas - totalDespesas),
          totalCaixaFormatado: formatPrice(totalCaixa),
        };

        setDadosRelatorio(dadosFormatados);
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRefBusca.current?.setErrors(errors);
        } else {
          const mensagem = getResponseErrors(error);
          addToast({
            type: 'error',
            title: 'Não foi possível carregar dados',
            description: mensagem,
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [addToast, optionsClinica, optionsUsuario],
  );

  const handleFecharModalPagamento = useCallback(async (load: boolean) => {
    setShowModalPagamento(false);
    if (load) {
      formRefBusca.current?.submitForm();
      setDadosPagamento(undefined);
    }
  }, []);

  const handlePagamento = useCallback(
    (dados: IDadosPagamentoOrigem) => {
      if (user.UsuCodigo === 1) {
        setDadosPagamento(dados);

        setShowModalPagamento(true);
      }
    },
    [user.UsuCodigo],
  );

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleOnChangeVazio = useCallback(() => {}, []);

  return (
    <Container fluid className="mt-2">
      {loading && <Loading />}
      {showModalPagamento && (
        <Modal
          show={showModalPagamento}
          titulo={dadosPagamento?.tituloPagamento || ''}
          handleFecharModal={() => setShowModalPagamento(false)}
        >
          <Pagamento
            handleFechar={handleFecharModalPagamento}
            tipo={
              dadosPagamento?.CprOrigem?.toString() ||
              ORIGEM_PAGAMENTO.SANGRIA.toString()
            }
            cprCodigo={dadosPagamento?.pagamentoSelecionado}
          />
        </Modal>
      )}
      {optionsClinica.length > 0 && user && (
        <>
          <div className="d-print-none bg-white border p-2 mb-2">
            <div className="p-2 mb-2 d-flex justify-content-between">
              <h2 id="topoBusca">Fechamento de Caixa</h2>
              <PageInfo
                descricao={informacoesPagina}
                permissao={TIPO_USUARIO.SUPERVISOR}
              />
            </div>

            <Form
              ref={formRefBusca}
              initialData={initialData}
              onSubmit={handleSubmitBusca}
            >
              <Row>
                <Col md={3}>
                  <Input
                    name="data"
                    label="Data"
                    type="date"
                    required
                    onBlur={handleChangeData}
                  />
                </Col>
                <Col md={3}>
                  <SelectSync
                    name="UsuCodigo"
                    label="Colaborador"
                    options={optionsUsuario}
                    isClearable
                    handleSelectChange={handleOnChangeVazio}
                    isLoading={loadingUsuario}
                  />
                </Col>
                <Col md={3}>
                  <SelectSync
                    name="CliCodigo"
                    label="Clinica"
                    options={optionsClinica}
                    disabled={user.UsuTipo !== TIPO_USUARIO.ADMINISTRADOR}
                    handleSelectChange={handleChangeClinica}
                  />
                </Col>
                <Col md={2}>
                  <br />
                  <Button type="submit" size="sm" variant="outline-secondary">
                    BUSCAR
                  </Button>
                </Col>
              </Row>
            </Form>
          </div>

          <HeaderRelatorio
            dataImpressao={dataExibe?.dataHora}
            titulo={`FECHAMENTO DE CAIXA ${
              nomeClinica.length > 0 ? `- ${nomeClinica} -` : '-'
            } ${dataExibe?.dataDiaSemana || ''}${
              nomeUsuario.length > 0 ? ` - ${nomeUsuario}` : ''
            }`}
          />

          <div className="bg-white border p-2">
            {dadosRelatorio && (
              <>
                <table
                  className="table table-hover table-sm table-font-small table-bordered"
                  style={{ borderWidth: '0px' }}
                >
                  <thead>
                    <tr className="text-center">
                      <th>Descrição</th>
                      <th>Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dadosRelatorio.despesas &&
                      dadosRelatorio.despesas.map(relatorio => (
                        <tr
                          key={`despesas-${relatorio.codigo}`}
                          style={{ fontSize: 'Small', height: '30px' }}
                        >
                          <td>{relatorio.descricao}</td>
                          <td>{relatorio.valorFormatado}</td>
                        </tr>
                      ))}
                    <tr>
                      <td>
                        <b>Valor Total das Despesas</b>
                      </td>
                      <td>
                        <b>{dadosRelatorio.totalDespesasFormatado}</b>
                      </td>
                    </tr>

                    <tr>
                      <td colSpan={2}>
                        <br />
                      </td>
                    </tr>

                    {dadosRelatorio.receitas &&
                      dadosRelatorio.receitas.map(relatorio => (
                        <tr
                          key={`receitas-${relatorio.codigo}`}
                          style={{ fontSize: 'Small', height: '30px' }}
                        >
                          <td>{relatorio.descricao}</td>
                          <td>{relatorio.valorFormatado}</td>
                        </tr>
                      ))}
                    <tr>
                      <td>
                        <b>Valor Total das Receitas</b>
                      </td>
                      <td>
                        <b>{dadosRelatorio.totalReceitasFormatado}</b>
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={2}>
                        <br />
                      </td>
                    </tr>
                    {dadosRelatorio.trocos &&
                      dadosRelatorio.trocos.map(relatorio => (
                        <tr
                          key={`trocos-${relatorio.codigo}`}
                          style={{ fontSize: 'Small', height: '30px' }}
                          className="text-primary"
                        >
                          <td>
                            <Badge
                              style={{ fontSize: '12px', paddingLeft: '0px' }}
                              className={`${
                                user.UsuCodigo === 1 ? '' : 'd-none'
                              }`}
                              title={`${relatorio.descricao}`}
                              onClick={
                                () =>
                                  handlePagamento({
                                    CprOrigem: ORIGEM_PAGAMENTO.TROCO,
                                    tituloPagamento: `${relatorio.descricao}`,
                                    pagamentoSelecionado: relatorio.codigo,
                                  })
                                // eslint-disable-next-line react/jsx-curly-newline
                              }
                            >
                              {relatorio.descricao}
                            </Badge>
                          </td>
                          <td>{relatorio.valorFormatado}</td>
                        </tr>
                      ))}
                    {dadosRelatorio.sangrias &&
                      dadosRelatorio.sangrias.map(relatorio => (
                        <tr
                          key={`sangrias-${relatorio.codigo}`}
                          style={{ fontSize: 'Small', height: '30px' }}
                          className="text-danger"
                        >
                          <td>
                            <Badge
                              style={{ fontSize: '12px', paddingLeft: '0px' }}
                              title={`${relatorio.descricao}`}
                              onClick={
                                () =>
                                  handlePagamento({
                                    CprOrigem: ORIGEM_PAGAMENTO.SANGRIA,
                                    tituloPagamento: `${relatorio.descricao}`,
                                    pagamentoSelecionado: relatorio.codigo,
                                  })
                                // eslint-disable-next-line react/jsx-curly-newline
                              }
                            >
                              {relatorio.descricao}
                            </Badge>
                          </td>
                          <td>{relatorio.valorFormatado}</td>
                        </tr>
                      ))}
                    <tr>
                      <td>
                        <b>
                          Dinheiro Caixa (Total Dinheiro + Troco - Sangria -
                          Despesas)
                        </b>
                      </td>
                      <td>
                        <b>{dadosRelatorio?.totalCaixaFormatado}</b>
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={2}>
                        <br />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <b>Total geral, Receita - Despesa</b>
                      </td>
                      <td>
                        <b>{dadosRelatorio?.totalGeralFormatado}</b>
                      </td>
                    </tr>
                  </tbody>
                </table>

                <Row className="mt-5">
                  <Col className="text-center">
                    _____________________________________
                    <br />
                    Administração
                  </Col>
                  <Col className="text-center">
                    _____________________________________
                    <br />
                    Caixa
                  </Col>
                </Row>
              </>
            )}
          </div>
        </>
      )}
    </Container>
  );
};

export default FechamentoCaixa;
